/**
 * The guest demo's client flow — one state machine from "Play now" to the end card (AD-794;
 * design `docs/design/guest-demo.md` §6.2, §6.3, §6.5, §7.1; story
 * `q-guest-demo-s5-client-guest-mode-and-end-card`).
 *
 *   booting ──config 404──▶ unavailable            (the main origin: the normal auth screen + a notice)
 *      ▼                                         (every pointer, touch included, plays — §6.3)
 *   starting ──201──▶ playing ──end(reason)──▶ ended ──playAgain──▶ starting
 *      │  ├──guestAtCapacity──▶ queued ──heartbeat: claim──▶ starting(claim) ──▶ playing
 *      │  │                        └──404 unknown (restart)──▶ retry
 *      │  ├──guestQueueFull──▶ full                (the only true "go away", §7.1)
 *      │  ├──guestThrottled──▶ (auto-retry, bounded) ──▶ retry
 *      │  └──turnstile / rate / network──▶ retry   (a button: fresh Turnstile pass, §7)
 *
 * PURE OF THE DOM: Turnstile, the clock-ish timers, the heartbeat ticker and the transport are
 * all injected, so every edge above is a unit test (`guestFlow.test.ts`). The guest token lives
 * ONLY in this object's state (page memory): nothing here writes storage or cookies.
 *
 * Every async continuation is fenced by an epoch: a response that arrives after the flow moved
 * on (the visitor clicked "Play again", or the page is tearing down) is dropped, never applied.
 */

import { GuestApiError } from './guestApi.js';
import type { GuestApi, GuestConfig, GuestSession } from './guestApi.js';
import type { Ticker } from './ticker.js';

/** Why the demo ended — the end card's framing (§6.4: "Time's up —" for `expired`). */
export type GuestEndReason = 'completed' | 'expired' | 'left' | 'lost';

export type GuestPhase =
  | { kind: 'booting' }
  | { kind: 'unavailable' }
  | { kind: 'starting' }
  | { kind: 'queued'; position: number; etaSeconds: number | null }
  | { kind: 'playing'; session: GuestSession }
  | { kind: 'ended'; reason: GuestEndReason }
  | { kind: 'full' }
  | { kind: 'retry'; code: string; message: string };

export interface GuestFlowDeps {
  api: GuestApi;
  /** A fresh Turnstile token (single-use), or undefined when no widget is configured (local). */
  verify: () => Promise<string | undefined>;
  /** Drives the queue heartbeat; the browser one keeps time in a background tab (`ticker.ts`). */
  ticker: Ticker;
  /** One-shot timer; returns its cancel. Injected so the throttle back-off is testable. */
  later: (fn: () => void, ms: number) => () => void;
  /**
   * Whether this page must reload before a start because the server runs a newer build
   * (`staleClient.ts`, q-live-sword-grip-still-open). Absent ⇒ never (tests, local tools).
   */
  staleClient?: () => Promise<boolean>;
  /** Reload the page (the browser's `location.reload`). Called only when `staleClient` says so. */
  reload?: () => void;
}

/** Back-off between automatic retries of a `guestThrottled` start (the process burst bucket). */
export const THROTTLE_RETRY_MS = 2_000;
/** Automatic `guestThrottled` retries before the visitor is shown a "Try again" button. */
export const THROTTLE_RETRY_LIMIT = 3;

/** The visitor-facing line for a refusal the flow cannot resolve on its own. */
export function retryMessage(code: string): string {
  switch (code) {
    case 'guestTurnstileFailed':
      return "The quick bot check didn't pass. Try again.";
    case 'guestTurnstileUnavailable':
      return "The bot check couldn't be reached just now. Try again in a moment.";
    case 'rateLimited':
      return 'Too many demo starts from your connection. Wait a moment, then try again.';
    case 'guestThrottled':
      return 'A lot of people are arriving at once. Try again in a moment.';
    case 'guestQueueUnknown':
      return 'The demo restarted, so the waiting line was cleared. Rejoin to take a new place.';
    case 'queueUnavailable':
      return 'The demo is full right now. Try again in a minute.';
    case 'network':
      return "Couldn't reach the demo. Check your connection and try again.";
    default:
      return 'Something went wrong starting the demo. Try again.';
  }
}

export class GuestFlow {
  private phase: GuestPhase = { kind: 'booting' };
  private cfg: GuestConfig | null = null;
  private readonly listeners = new Set<(phase: GuestPhase) => void>();
  /** Bumped whenever the flow moves to a new attempt or is disposed; fences stale continuations. */
  private epoch = 0;
  private stopBeat: (() => void) | null = null;
  private cancelLater: (() => void) | null = null;
  private queueId: string | null = null;
  private beating = false;
  private throttleRetries = 0;

  constructor(private readonly deps: GuestFlowDeps) {}

  get state(): GuestPhase {
    return this.phase;
  }

  /** The served launch config, once `boot()` has read it (null before, or when unavailable). */
  get config(): GuestConfig | null {
    return this.cfg;
  }

  subscribe(listener: (phase: GuestPhase) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private set(next: GuestPhase): void {
    this.phase = next;
    for (const listener of this.listeners) listener(next);
  }

  /** Read the launch config, then start — or stop at `unavailable`. */
  async boot(): Promise<void> {
    const epoch = ++this.epoch;
    this.set({ kind: 'booting' });
    try {
      const cfg = await this.deps.api.config();
      if (epoch !== this.epoch) return;
      this.cfg = cfg;
    } catch (error) {
      if (epoch !== this.epoch) return;
      const code = error instanceof GuestApiError ? error.code : 'unknown';
      const status = error instanceof GuestApiError ? error.status : 0;
      // The route is ABSENT on the main service (404): this is not a guest origin.
      if (status === 404) this.set({ kind: 'unavailable' });
      else this.set({ kind: 'retry', code, message: retryMessage(code) });
      return;
    }
    await this.start();
  }

  /** A fresh start: a new Turnstile pass, then `POST /api/guest/start`. Also "Play again". */
  async start(): Promise<void> {
    if (this.cfg === null) {
      await this.boot();
      return;
    }
    const epoch = this.reset();
    this.set({ kind: 'starting' });
    // A page left open across a deploy would start the new session on the OLD client (Play again
    // never reloads): ask first, and reload into the deployed bundle instead.
    if (this.deps.staleClient !== undefined && this.deps.reload !== undefined) {
      const stale = await this.deps.staleClient().catch(() => false);
      if (epoch !== this.epoch) return;
      if (stale) {
        this.deps.reload();
        return;
      }
    }
    await this.attemptStart(epoch, null);
  }

  private reset(): number {
    this.stopHeartbeat();
    this.cancelLater?.();
    this.cancelLater = null;
    this.queueId = null;
    return ++this.epoch;
  }

  private async attemptStart(epoch: number, claimTicket: string | null): Promise<void> {
    let session: GuestSession;
    try {
      if (claimTicket !== null) {
        // The ticket is the credential: Turnstile was passed at queue entry (§7.1).
        session = await this.deps.api.start({ claimTicket });
      } else {
        const turnstileToken = await this.deps.verify();
        if (epoch !== this.epoch) return;
        session = await this.deps.api.start(turnstileToken === undefined ? {} : { turnstileToken });
      }
    } catch (error) {
      if (epoch !== this.epoch) return;
      await this.onStartRefused(epoch, error, claimTicket !== null);
      return;
    }
    if (epoch !== this.epoch) return;
    this.throttleRetries = 0;
    this.set({ kind: 'playing', session });
  }

  private async onStartRefused(epoch: number, error: unknown, wasClaim: boolean): Promise<void> {
    const code = error instanceof GuestApiError ? error.code : 'unknown';
    const status = error instanceof GuestApiError ? error.status : 0;
    if (code === 'guestAtCapacity') {
      await this.joinQueue(epoch);
      return;
    }
    if (code === 'guestQueueFull') {
      this.set({ kind: 'full' });
      return;
    }
    if (code === 'guestClaimInvalid' && wasClaim) {
      // The held place lapsed before we redeemed it: back of the line with a fresh pass.
      await this.joinQueue(epoch);
      return;
    }
    if (code === 'guestThrottled' && this.throttleRetries < THROTTLE_RETRY_LIMIT) {
      this.throttleRetries += 1;
      this.cancelLater = this.deps.later(() => {
        this.cancelLater = null;
        if (epoch === this.epoch) void this.attemptStart(epoch, null);
      }, THROTTLE_RETRY_MS * this.throttleRetries);
      return;
    }
    if (status === 404) {
      this.set({ kind: 'unavailable' });
      return;
    }
    this.throttleRetries = 0;
    this.set({ kind: 'retry', code, message: retryMessage(code) });
  }

  private async joinQueue(epoch: number): Promise<void> {
    let entry;
    try {
      const turnstileToken = await this.deps.verify();
      if (epoch !== this.epoch) return;
      entry = await this.deps.api.queueJoin(turnstileToken === undefined ? {} : { turnstileToken });
    } catch (error) {
      if (epoch !== this.epoch) return;
      const code = error instanceof GuestApiError ? error.code : 'unknown';
      const status = error instanceof GuestApiError ? error.status : 0;
      if (code === 'guestQueueFull') {
        this.set({ kind: 'full' });
        return;
      }
      // A guest process without the queue (S3 not yet deployed) answers 404 here.
      const shown = status === 404 ? 'queueUnavailable' : code;
      this.set({ kind: 'retry', code: shown, message: retryMessage(shown) });
      return;
    }
    if (epoch !== this.epoch) return;
    this.queueId = entry.queueId;
    this.set({ kind: 'queued', position: entry.position, etaSeconds: entry.etaSeconds });
    this.stopBeat = this.deps.ticker.start(this.cfg?.queueHeartbeatMs ?? 15_000, () => {
      void this.beat(epoch);
    });
  }

  /** One heartbeat. Overlapping beats are skipped; a transient network error just waits for the next. */
  async beat(epoch: number = this.epoch): Promise<void> {
    if (this.beating || this.queueId === null || epoch !== this.epoch) return;
    if (this.phase.kind !== 'queued') return;
    this.beating = true;
    let beat;
    try {
      beat = await this.deps.api.queueHeartbeat(this.queueId);
    } catch (error) {
      this.beating = false;
      if (epoch !== this.epoch) return;
      const code = error instanceof GuestApiError ? error.code : 'unknown';
      const status = error instanceof GuestApiError ? error.status : 0;
      if (status === 404 || code === 'guestQueueUnknown') {
        // Dropped (or the process restarted): the place is gone — say so and offer a rejoin.
        this.stopHeartbeat();
        this.queueId = null;
        this.set({
          kind: 'retry',
          code: 'guestQueueUnknown',
          message: retryMessage('guestQueueUnknown'),
        });
      }
      return;
    }
    this.beating = false;
    if (epoch !== this.epoch) return;
    if (beat.status === 'waiting') {
      this.set({ kind: 'queued', position: beat.position, etaSeconds: beat.etaSeconds });
      return;
    }
    // A seat is ours: stop beating and redeem the single-use ticket at once.
    this.stopHeartbeat();
    this.queueId = null;
    this.set({ kind: 'starting' });
    await this.attemptStart(epoch, beat.claimTicket);
  }

  /**
   * The demo is over (turn-in, TTL, the player left, or the connection is gone). Idempotent and
   * only from `playing`. Tells the server best-effort (`POST /api/guest/end` tears the instance
   * down and forgets the rows); the card shows whether or not that call lands — at worst the
   * server's own TTL ends the instance.
   */
  end(reason: GuestEndReason): void {
    if (this.phase.kind !== 'playing') return;
    // q-lane-guest-demo-ended-mid-walk: which trigger ended it is otherwise unrecorded (the
    // server logs every end as `completed`). One line, no identifier.
    console.info(`[guest] demo ended: ${reason}`);
    const token = this.phase.session.token;
    this.reset();
    this.set({ kind: 'ended', reason });
    void this.deps.api.end(token).catch(() => undefined);
  }

  /** "Play again" / "Try again" / "Rejoin": a fresh start (a fresh Turnstile pass). */
  playAgain(): void {
    this.throttleRetries = 0;
    void this.start();
  }

  /** Tear down: stop the heartbeat and every pending continuation. */
  dispose(): void {
    this.reset();
    this.listeners.clear();
  }

  private stopHeartbeat(): void {
    this.stopBeat?.();
    this.stopBeat = null;
    this.beating = false;
  }
}
